/**
 * The rules that drive animals — M2-05's systems (state lives in `mind.ts` and
 * `ecology.ts`; this is the rules over it).
 *
 * Split for the reason `combat.ts` is split from `fighter.ts`, and it is the
 * same rule stated again: **world state names these types, and the rules read
 * world state, and nothing points back down**. `mind.ts` holds what an animal
 * *is* — its instinct, its memory, the morale arithmetic — and depends on
 * nothing but the body. Everything here depends on the world, the catalogue and
 * the intent handlers, and nothing depends on it but the rule set.
 *
 * That is not tidiness. `world.ts` has to declare `Mind`, `Den` and `Carcass`
 * to store them, so any module those types live in cannot import world state,
 * or the graph closes into a cycle and the build order stops being a fact.
 */
import { chebyshev, DIRECTION_VECTORS, DIRECTIONS, } from './actor.js';
import { attackHandler, disengageHandler, engageHandler } from './combat.js';
import { BEAST_CEILING, denIsDue, isGone, reconcileDen } from './ecology.js';
import { canForage, floraKey, take as takeFlora } from './flora.js';
import { isPassable } from './map.js';
import { canMoveAt, findSpawn, joinHandler, moveHandler } from './movement.js';
import { activityFor, morale, packDownFor, packQuarry, willFight, } from './mind.js';
import { abandonsErrand, takeCarcass } from './raids.js';
import { perceive, sensesOf } from './senses.js';
import { TICKS_PER_GAME_MINUTE } from './time.js';
import { bloodOf, die, isDown } from './vitals.js';
import { daylight } from './weather.js';
/* -------------------------------------------------------------------------- *
 * Acting — through the same door a player uses
 * -------------------------------------------------------------------------- */
/**
 * Put an intent through its handler's own `check` and `apply`.
 *
 * The one function that makes "creatures use the intent API" true rather than
 * aspirational. It returns whether the action happened, so the caller can fall
 * through to a second choice — a wolf refused a bite because somebody is
 * already three-deep on its quarry should go looking for a flank, not stand
 * still having spent its tick.
 */
function act(ctx, handler, intent) {
    if (handler.check?.(intent, ctx) != null)
        return false;
    handler.apply(intent, ctx);
    return true;
}
function moveIntent(actorId, tick, direction) {
    return { actorId, tick, type: 'move', payload: { direction } };
}
function attackIntent(actorId, tick, request) {
    return { actorId, tick, type: 'attack', payload: request };
}
function engageIntent(actorId, tick, request) {
    return {
        actorId,
        tick,
        type: 'engage',
        payload: { targetId: request.targetId, direction: request.direction },
    };
}
/** A creature chooses to close just as explicitly as a player does. */
function bite(ctx, actor, targetId) {
    const combatant = ctx.world.combat[actor.id];
    if (combatant?.engagedWith !== targetId) {
        return act(ctx, engageHandler, engageIntent(actor.id, ctx.tick, { targetId, direction: null, part: null }));
    }
    return act(ctx, attackHandler, attackIntent(actor.id, ctx.tick, { targetId, direction: null, part: null }));
}
/**
 * Directions ordered by how much closer to `(toX, toY)` each would leave you,
 * best first. `away` inverts it into a flight.
 *
 * Deterministic and total: every one of the eight is returned, so a step is
 * refused only when all eight are genuinely blocked, and an animal in a corner
 * squeezes out along the wall instead of standing there being killed.
 */
export function stepsToward(from, toX, toY, away = false) {
    const scored = DIRECTIONS.map((direction) => {
        const [dx, dy] = DIRECTION_VECTORS[direction];
        const distance = chebyshev(from.x + dx, from.y + dy, toX, toY);
        return { direction, distance: away ? -distance : distance };
    });
    // Ties by compass order, so two identical wolves in identical spots do not
    // depend on `sort` stability to agree between replays.
    return scored
        .sort((a, b) => a.distance - b.distance ||
        DIRECTIONS.indexOf(a.direction) - DIRECTIONS.indexOf(b.direction))
        .map((entry) => entry.direction);
}
/** Try to step toward or away from a point. False if every direction is refused. */
function walk(ctx, actor, toX, toY, away = false) {
    if (!canMoveAt(actor, ctx.tick))
        return false;
    for (const direction of stepsToward(actor, toX, toY, away)) {
        if (act(ctx, moveHandler, moveIntent(actor.id, ctx.tick, direction)))
            return true;
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * The system
 * -------------------------------------------------------------------------- */
/** Is this actor something an animal should have an opinion about? */
function isThreat(actor) {
    return actor.kind === 'player';
}
/**
 * Is this one of the village's animals (M5-01)?
 *
 * Read off the *disposition* rather than off a list of species ids, so the day
 * content adds goats there is nothing here to update. `docile` means bred out
 * of fear, and a thing bred out of fear is a thing that lives behind a fence
 * because it cannot look after itself.
 */
function isLivestock(ctx, actor) {
    const mind = ctx.world.minds[actor.id];
    if (!mind)
        return false;
    return ctx.catalogue.instinct?.(mind.instinctId)?.disposition === 'docile';
}
/**
 * What this animal will form a plan about.
 *
 * Everything reacts to people. A predator *additionally* reacts to livestock,
 * and that one line is what the wolf raids of M5-02 are built on: a pack that
 * came over the fence for the sheep is a pack with a reason to be in the
 * village, rather than a pack that wandered in and found a man.
 *
 * Deliberately not "predators hunt all prey". A wolf that treated every rabbit
 * and stag in the commote as quarry would spend the whole game chasing them and
 * never be where a player is, and the valley's wildlife would stop being
 * wildlife and start being a food chain simulation nobody asked for.
 */
function noticeable(ctx, actor, instinct, laden) {
    if (isThreat(actor))
        return true;
    // A wolf with a carcass on its back has what it came for and stops shopping
    // (M5-02). Without this a raiding pack would kill the whole fold one animal
    // at a time and never leave with any of it, which is both worse for the
    // village and a less interesting thing to walk out of your door and see.
    if (laden)
        return false;
    return instinct.disposition === 'predator' && isLivestock(ctx, actor);
}
/**
 * Take the kill, if there is one at its feet and its jaws are free (M5-02).
 *
 * The wolf half of the drag; `takeCarcass` is the state change and this is the
 * announcement, which belongs here because this module is the one that knows
 * what an animal doing something looks like in the log.
 */
function claimKill(ctx, actor, mind) {
    if (ctx.world.combat[actor.id]?.burden)
        return;
    const took = takeCarcass(ctx.world, actor, ctx.tick);
    if (took === null)
        return;
    // It is leaving, and it stops being interested in anything else on the way.
    mind.targetId = null;
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['raid', 'drags', took],
        text: `${actor.name} takes hold of the carcass and starts to drag it away.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/**
 * One animal's tick: look, decide, do one thing.
 *
 * The order is fixed and load-bearing — perceive, then transition, then act —
 * because an animal that acted on a state it had not yet updated would spend
 * one tick a second doing the previous tick's plan, and at ten ticks a second
 * that reads to a player as an animal that is somehow always a beat behind.
 */
function think(ctx, actor, mind) {
    const instinct = ctx.catalogue.instinct?.(mind.instinctId);
    if (!instinct)
        return;
    const combatant = ctx.world.combat[actor.id];
    const plan = combatant ? ctx.catalogue.plan(combatant.loadout.planId) : null;
    // The dead and the senseless do not have plans.
    if (combatant?.vitals.dead)
        return;
    if (plan && combatant && isDown(plan, combatant.vitals, ctx.tick))
        return;
    // The wind comes off world state now (M3-05): approaching a stag downwind is
    // a thing a hunter has to *check* rather than a fact about the map, because
    // the quarter it blows from veers over the hours.
    const senses = sensesOf(instinct.senses, plan, combatant);
    const laden = combatant?.burden != null;
    const seen = perceive(ctx.world, actor, senses, ctx.world.weather.wind).filter((detection) => {
        const other = ctx.world.actors[detection.actorId];
        return other !== undefined && other.id !== actor.id && noticeable(ctx, other, instinct, laden);
    });
    // An errand is a fact about the animal's night rather than about what it is
    // doing this second, so it is ended *here* — before any state is chosen —
    // and not inside the grazing walk that consults it. A wolf that reached the
    // fold and is now in a fight has finished its errand whatever happens next,
    // and one left carrying a stale one could never be sent on another raid,
    // because `ableWolves` reads exactly this field to decide who is free.
    if (mind.errand && abandonsErrand(ctx, actor, mind))
        mind.errand = null;
    // It has killed something and is standing over it: take it and go (M5-02).
    // Checked here rather than inside the grazing walk, because the tick a wolf
    // picks a carcass up is the tick after it stopped fighting — and it is
    // usually still surrounded by the rest of the herd, so it never grazes at
    // all until it has something on its back.
    if (mind.errand)
        claimKill(ctx, actor, mind);
    remember(ctx, mind, seen);
    if (leavesDownedThreat(ctx, actor, mind, instinct))
        return;
    transition(ctx, actor, mind, instinct);
    perform(ctx, actor, mind, instinct);
}
/** Update what the animal knows, and pick up the pack's quarry if it has none. */
function remember(ctx, mind, seen) {
    const nearest = seen[0];
    if (nearest) {
        const known = seen.find((detection) => detection.actorId === mind.targetId) ??
            burdened(ctx, seen) ??
            nearest;
        const target = ctx.world.actors[known.actorId];
        if (target) {
            mind.targetId = known.actorId;
            mind.seenAt = ctx.tick;
            mind.seenX = target.x;
            mind.seenY = target.y;
            return;
        }
    }
    // Nothing perceived. Take the pack's word for it if the pack has one — a
    // wolf that hears the others go in does not need to have seen you itself.
    if (mind.targetId === null) {
        const quarry = packQuarry(ctx.world, mind.packId);
        const target = quarry ? ctx.world.actors[quarry] : undefined;
        if (target) {
            mind.targetId = quarry;
            mind.seenAt = ctx.tick;
            mind.seenX = target.x;
            mind.seenY = target.y;
        }
    }
}
/**
 * The one in the group with their arms full (M4-06).
 *
 * A wolf picks the weak one out of a group, and a man carrying his friend on
 * his shoulder is visibly the weak one: he is slow, both his hands are full and
 * he cannot swing at anything. Preferred over the nearest rather than weighted
 * into a score, because that is how it reads at the range this matters — the
 * pack does not split the difference, it goes for the straggler.
 *
 * This is what turns the rescue march into a running fight, which the ticket
 * asks for and which is the point of carrying anybody anywhere.
 */
function burdened(ctx, seen) {
    return seen.find((detection) => ctx.world.combat[detection.actorId]?.burden != null);
}
function enter(ctx, actor, mind, state, instinct) {
    if (mind.state === state)
        return;
    const was = mind.state;
    mind.state = state;
    mind.since = ctx.tick;
    ctx.emit({
        type: 'status',
        actors: [actor.id],
        refs: ['beast', state, was],
        text: transitionText(actor.name, state),
        // Ordinary wildlife stays quiet telemetry. The apex's escalation beats are
        // the encounter warning, so nearby players must actually receive them.
        audience: state === 'flee' || (instinct.apex === true && state !== 'graze') ? 'witnesses' : 'archive',
        at: { x: actor.x, y: actor.y },
    });
}
function transitionText(name, state) {
    switch (state) {
        case 'alert':
            return `${name} lifts its head.`;
        case 'stalk':
            return `${name} begins to close.`;
        case 'fight':
            return `${name} comes in.`;
        case 'flee':
            return `${name} breaks off and runs.`;
        default:
            return `${name} settles.`;
    }
}
/** Whether the animal still believes in a target it may not currently perceive. */
function believes(mind, instinct, tick) {
    return mind.targetId !== null && tick - mind.seenAt <= instinct.memory;
}
/**
 * A territorial animal has removed the threat once that person is helpless.
 * Predators may still finish prey; a boar defends its ground and leaves a body
 * where it fell. This also prevents an unavoidable second strike from turning
 * a severe knockdown into an automatic execution.
 */
function leavesDownedThreat(ctx, actor, mind, instinct) {
    if (instinct.disposition !== 'territorial' || mind.targetId === null)
        return false;
    const target = ctx.world.combat[mind.targetId];
    const plan = target ? ctx.catalogue.plan(target.loadout.planId) : null;
    if (!target || !plan || !isDown(plan, target.vitals, ctx.tick))
        return false;
    const combatant = ctx.world.combat[actor.id];
    if (combatant?.engagedWith !== null) {
        act(ctx, disengageHandler, {
            actorId: actor.id,
            tick: ctx.tick,
            type: 'disengage',
            payload: {},
        });
    }
    mind.targetId = null;
    mind.guardedBy = null;
    enter(ctx, actor, mind, 'graze', instinct);
    return true;
}
/**
 * Has it bled past the point where it will keep pressing (M5-04)?
 *
 * True only for an animal that declares a `retreatBelowBlood` — which is
 * Ysgithyrwyn and, for now, nothing else — and only once its blood is under
 * that mark. Everything that flees the ordinary way breaks on nerve long before
 * its blood runs this low, so this is dead weight for a wolf and the whole of
 * the second act for the boar.
 */
function retreating(ctx, actor, instinct) {
    const threshold = instinct.retreatBelowBlood;
    if (threshold === undefined)
        return false;
    const combatant = ctx.world.combat[actor.id];
    if (!combatant)
        return false;
    return bloodOf(combatant.vitals) < threshold;
}
function transition(ctx, actor, mind, instinct) {
    if (!believes(mind, instinct, ctx.tick)) {
        mind.targetId = null;
        mind.guardedBy = null;
        enter(ctx, actor, mind, 'graze', instinct);
        return;
    }
    const distance = chebyshev(actor.x, actor.y, mind.seenX, mind.seenY);
    // Once running, keep running until the thing is out of mind entirely. An
    // animal that re-committed the moment its nerve ticked back over zero would
    // yo-yo in and out of the fight, which reads as a bug rather than as courage.
    if (mind.state === 'flee')
        return;
    // Bled past the point it can keep it up: it breaks for home, even enraged
    // (M5-04). Ahead of the morale read because this is precisely what overrides
    // morale — the boar's nerve has not failed, its body has, and `flee` here is
    // the fighting retreat `perform` reads back off this same condition.
    if (retreating(ctx, actor, instinct)) {
        enter(ctx, actor, mind, 'flee', instinct);
        return;
    }
    if (mind.state === 'fight' || mind.state === 'stalk') {
        const reading = readMorale(ctx, actor, mind, instinct);
        if (!willFight(reading)) {
            enter(ctx, actor, mind, 'flee', instinct);
            return;
        }
    }
    switch (instinct.disposition) {
        case 'docile':
            // It has never had to run from anything and it does not start now. A cow
            // that bolted at the sight of a man would not be a cow, and — the part
            // that matters for M5-02 — an animal that fled the pen at the first wolf
            // would make the raid a chase rather than a slaughter. It goes on eating
            // until you are on top of it, moves off a step when you are, and fights
            // only when the fence behind it means there is nowhere left to move to.
            if (distance <= 1 && cornered(ctx, actor))
                enter(ctx, actor, mind, 'fight', instinct);
            else if (distance <= instinct.flightAt)
                enter(ctx, actor, mind, 'flee', instinct);
            else
                enter(ctx, actor, mind, 'graze', instinct);
            return;
        case 'prey':
            // It does not want a fight and will only take one it cannot leave.
            if (distance <= 1 && cornered(ctx, actor))
                enter(ctx, actor, mind, 'fight', instinct);
            else if (distance <= instinct.flightAt)
                enter(ctx, actor, mind, 'flee', instinct);
            else
                enter(ctx, actor, mind, 'alert', instinct);
            return;
        case 'territorial':
            // Content to be left alone, and absolutely committed once it is not.
            if (distance <= instinct.closeAt)
                enter(ctx, actor, mind, 'fight', instinct);
            else if (distance <= instinct.flightAt)
                enter(ctx, actor, mind, 'stalk', instinct);
            else
                enter(ctx, actor, mind, 'alert', instinct);
            return;
        default:
            if (distance <= instinct.closeAt)
                enter(ctx, actor, mind, 'fight', instinct);
            else
                enter(ctx, actor, mind, 'stalk', instinct);
    }
}
function readMorale(ctx, actor, mind, instinct) {
    return morale({
        instinct,
        combatant: ctx.world.combat[actor.id],
        packDown: packDownFor(ctx.world, ctx.catalogue.plan, actor.id, mind.packId, ctx.tick),
        enemies: enemiesOn(ctx, actor),
        fightingFor: ctx.tick - mind.since,
        cornered: cornered(ctx, actor),
        // M5-03. A pack defends its own doorstep harder than it defends a hillside.
        atHome: atHome(ctx, actor),
    });
}
/**
 * How near its own den an animal has to be for that to count (M5-03).
 *
 * Four tiles, which is the mouth of the den and the ground immediately outside
 * it — near enough that "they are defending the den" is what a player watching
 * would say, and not so far that a wolf caught halfway up the hill gets the
 * bonus for having been born nearby.
 */
export const HOME_GROUND_RANGE = 4;
/** Is this animal standing on the ground it lives on? */
function atHome(ctx, actor) {
    // Read off the den it belongs to rather than off `mind.homeX`, because the
    // mind's home is wherever the animal happened to be *placed* — for livestock
    // that is the fold, and a cow does not get a morale bonus for standing in it.
    for (const den of Object.values(ctx.world.dens)) {
        if (!den.living.includes(actor.id))
            continue;
        return chebyshev(actor.x, actor.y, den.x, den.y) <= HOME_GROUND_RANGE;
    }
    return false;
}
/** How many people are within a stride of this animal. The odds it can feel. */
function enemiesOn(ctx, actor) {
    let count = 0;
    for (const other of Object.values(ctx.world.actors)) {
        if (other.id === actor.id || !isThreat(other))
            continue;
        if (chebyshev(actor.x, actor.y, other.x, other.y) <= 2)
            count += 1;
    }
    return count;
}
/**
 * Nowhere to go: every direction is wall or body.
 *
 * An animal that cannot leave does not get to decide whether to fight, which is
 * both true and the reason a boar in a gully is the worst thing in the valley.
 */
export function cornered(ctx, actor) {
    for (const direction of DIRECTIONS) {
        const [dx, dy] = DIRECTION_VECTORS[direction];
        const x = actor.x + dx;
        const y = actor.y + dy;
        if (!isPassableTile(ctx, x, y))
            continue;
        if (occupied(ctx, x, y))
            continue;
        return false;
    }
    return true;
}
function isPassableTile(ctx, x, y) {
    return isPassable(ctx.world.map, x, y);
}
function occupied(ctx, x, y) {
    for (const other of Object.values(ctx.world.actors)) {
        if (other.x === x && other.y === y)
            return true;
    }
    return false;
}
/**
 * A wounded monster making for its lair, goring what blocks the way (M5-04).
 *
 * Not the ordinary `flee`, which walks away from what frightened it and never
 * strikes: this heads for *home* — the wallow — and takes a swing at whatever it
 * has already fixed on if that thing is in reach, because a boar breaking off is
 * not a boar you can walk beside. The blood it leaves on the way is emitted by
 * the move itself (M3-08), heavy because the animal is heavy, and following it
 * is the hunt's second act.
 */
function fightingRetreat(ctx, actor, mind) {
    if (mind.targetId !== null && bite(ctx, actor, mind.targetId)) {
        return;
    }
    walk(ctx, actor, mind.homeX, mind.homeY);
}
/** Do the one thing this tick's state calls for. */
function perform(ctx, actor, mind, instinct) {
    // A fighting retreat is neither of the walks the switch knows: it makes for
    // home rather than for the target, whatever state the transition left it in.
    // Read off the same blood condition that put it into `flee` (M5-04).
    if (mind.state === 'flee' && retreating(ctx, actor, instinct)) {
        fightingRetreat(ctx, actor, mind);
        return;
    }
    switch (mind.state) {
        case 'fight': {
            if (mind.targetId === null)
                return;
            if (ctx.world.combat[actor.id]?.engagedWith === mind.targetId &&
                ctx.tick - mind.since < (instinct.warningTicks ?? 0)) {
                return;
            }
            // Bite first; if the front is full, walk — which is where flanking comes
            // from, because the engagement cap is what refused the bite.
            if (bite(ctx, actor, mind.targetId)) {
                return;
            }
            walk(ctx, actor, mind.seenX, mind.seenY);
            return;
        }
        case 'stalk':
            if (ctx.tick - mind.since < (instinct.warningTicks ?? 0)) {
                face(actor, mind.seenX, mind.seenY);
                return;
            }
            walk(ctx, actor, mind.seenX, mind.seenY);
            return;
        case 'flee': {
            // Flight is an actual break-off, not an intention to walk that the
            // engagement rule will refuse forever. The exposed eight-tick withdrawal
            // remains: a frightened animal can be caught while turning away, but once
            // it gets clear it must be able to run.
            const combatant = ctx.world.combat[actor.id];
            if (combatant?.withdrawingUntil !== 0)
                return;
            // Closing to melee buys an exchange, not a race against the input
            // latency. A creature that has been engaged must first face one resolved
            // blow (a hit, parry, or dodge all count) before it can start to break
            // off. Until then it holds the tile, and the player has a reliable `A`
            // strike instead of trying to read an aim prompt before a rabbit runs.
            if (combatant?.engagedWith !== null && combatant?.defences.length === 0)
                return;
            if (act(ctx, disengageHandler, {
                actorId: actor.id,
                tick: ctx.tick,
                type: 'disengage',
                payload: {},
            })) {
                return;
            }
            // Away from what frightened it, and failing that toward home — an animal
            // backed into a corner still tries, which is what makes `cornered` mean
            // something rather than being a state it silently sits in.
            if (walk(ctx, actor, mind.seenX, mind.seenY, true))
                return;
            walk(ctx, actor, mind.homeX, mind.homeY);
            return;
        }
        case 'alert':
            // Watching. Turning to face is free and is the tell a player reads.
            face(actor, mind.seenX, mind.seenY);
            return;
        default:
            graze(ctx, actor, mind, instinct);
    }
}
/* -------------------------------------------------------------------------- *
 * Grazing: a home range and somewhere to be going (M3-10)
 * -------------------------------------------------------------------------- */
/**
 * How long an animal stands still on arriving somewhere, at full activity.
 *
 * Ten seconds of real time, and it is the number that makes wandering read as
 * *feeding* rather than as patrolling: an animal that walked continuously would
 * look like it was on its way somewhere, and nothing in the valley is.
 */
export const WANDER_PAUSE_TICKS = TICKS_PER_GAME_MINUTE * 4;
/**
 * How many candidate tiles a wander roll looks at.
 *
 * Three, because the point of more than one is not to find a *better* tile but
 * to prefer a tile with something growing on it — and a browsing animal that
 * sampled the whole home range would end up standing on the single best patch
 * in it for ever, which is a pathfinder rather than a deer.
 */
export const WANDER_TRIES = 3;
/**
 * Walk toward whatever this animal set out to do, and pick up what it came for.
 *
 * Returns `false` when there is nothing left to walk toward, so the caller
 * falls through to ordinary grazing on the same tick rather than standing about
 * for one. What *ends* an errand is `think`'s business, not this function's —
 * see `abandonsErrand`.
 *
 * Arriving is not "reached the tile": it is *having something in front of it*,
 * which the ordinary perception loop decides. So this only ever runs while the
 * animal has seen nothing worth attending to, and the instant it does, the
 * state machine takes over and the errand simply stops being consulted.
 */
function runErrand(ctx, actor, mind) {
    const errand = mind.errand;
    if (!errand)
        return false;
    if (actor.x === errand.x && actor.y === errand.y) {
        // Arrived and found nothing. There is no third thing for it to do here.
        mind.errand = null;
        return false;
    }
    return walk(ctx, actor, errand.x, errand.y);
}
/**
 * Stand, feed, walk somewhere else, stand again.
 *
 * The fix M3-10 is built on. This used to walk only when the animal had drifted
 * further from home than `flightAt` — and since it spawns *at* home the distance
 * was zero, so ten beasts ran for six game hours and moved nothing. Grazing was
 * a leash with nothing to leash against.
 *
 * What it needs instead is something to pull against, which is a destination:
 * the animal walks to a tile inside its home range, pauses, and picks another.
 * Everything else in the ticket falls out of that one loop — sign is emitted by
 * movement (M3-08), so animals that walk are animals that can be tracked.
 */
function graze(ctx, actor, mind, instinct) {
    // Somewhere to be (M5-02). Checked before the home-range leash, because the
    // whole point of an errand is that it is the one thing that takes an animal
    // off its own ground — a wolf on a raid is two hundred tiles outside its home
    // range by design, and the leash would otherwise turn it round at the fence.
    if (mind.errand && runErrand(ctx, actor, mind))
        return;
    // Beyond its range, whatever it was doing: come back. This is the leash the
    // old code was reaching for, stated as its own rule rather than smuggled into
    // the flight distance — a wounded animal that fled across the valley walks
    // home rather than settling wherever it stopped.
    const fromHome = chebyshev(actor.x, actor.y, mind.homeX, mind.homeY);
    if (fromHome > instinct.homeRange) {
        walk(ctx, actor, mind.homeX, mind.homeY);
        return;
    }
    if (ctx.tick < mind.pauseUntil)
        return;
    // Asked before anything is rolled, so an animal on its movement cooldown
    // costs one comparison rather than three draws from its own stream.
    if (!canMoveAt(actor, ctx.tick))
        return;
    const activity = activityFor(instinct, daylight(ctx.time));
    if (actor.x === mind.wanderX && actor.y === mind.wanderY) {
        // Arrived. Stand a while — longer the quieter the hour — and then decide
        // where to go next. What it eats while standing here is the hourly
        // `browseSystem`'s business, not this tick's.
        mind.pauseUntil = ctx.tick + pauseTicks(activity);
        chooseWander(ctx, actor, mind, instinct, activity);
        return;
    }
    if (walk(ctx, actor, mind.wanderX, mind.wanderY))
        return;
    // Every direction refused: something is in the way that was not when it set
    // off. Pause and pick somewhere else rather than shoving at it every tick.
    mind.pauseUntil = ctx.tick + pauseTicks(activity);
    chooseWander(ctx, actor, mind, instinct, activity);
}
/** How long it stands still. Quiet hours are long ones. */
function pauseTicks(activity) {
    return Math.round(WANDER_PAUSE_TICKS / activity);
}
/**
 * Somewhere inside the home range to be going next.
 *
 * Drawn from the animal's own seeded stream, like every other roll in the
 * engine, so a replay puts every rabbit back on the same tuft of grass. A
 * browsing animal prefers a tile with something on it to eat, which is what
 * makes "the stag is here because there is food here" true rather than narrated.
 */
function chooseWander(ctx, actor, mind, instinct, activity) {
    // The clock, as a multiplier on distance rather than on existence. At its
    // quietest an animal shuffles a tile or two; at dawn it works the whole range.
    const radius = Math.max(1, Math.round(instinct.homeRange * activity));
    const dice = ctx.rng(`wander:${actor.id}`);
    let chosen = null;
    for (let attempt = 0; attempt < WANDER_TRIES; attempt += 1) {
        const x = mind.homeX + dice.int(-radius, radius + 1);
        const y = mind.homeY + dice.int(-radius, radius + 1);
        if (!isPassable(ctx.world.map, x, y))
            continue;
        chosen ??= { x, y };
        if (instinct.browses && canForage(ctx.world.flora[floraKey(x, y)])) {
            chosen = { x, y };
            break;
        }
    }
    // Nowhere legal came up — a warren against a crag, on an unlucky roll. Home
    // is always somewhere it can stand, because it is standing there.
    mind.wanderX = chosen?.x ?? mind.homeX;
    mind.wanderY = chosen?.y ?? mind.homeY;
}
function face(actor, x, y) {
    const dx = x - actor.x;
    const dy = y - actor.y;
    if (dx === 0 && dy === 0)
        return;
    const ns = dy < 0 ? 'n' : dy > 0 ? 's' : '';
    const ew = dx > 0 ? 'e' : dx < 0 ? 'w' : '';
    actor.facing = (ns + ew || actor.facing);
}
/* -------------------------------------------------------------------------- *
 * Thinking on a cadence (M3-10)
 * -------------------------------------------------------------------------- */
/**
 * How far away a player has to be before an animal is allowed to think slowly.
 *
 * Comfortably beyond the widest sight in the game — a stag's fourteen tiles,
 * doubled upwind — so nothing a player can perceive is ever on the cheap path.
 * The saving comes from the eighty animals in the other nine tenths of the
 * commote, which is where nearly all of them always are.
 */
export const THINK_NEAR = 40;
/**
 * Ticks between thoughts for an animal nobody is near.
 *
 * Ten: once a game second. A grazing animal's decisions are "walk a tile" and
 * "stand still for half a minute", and neither is improved by being revisited
 * ten times a second. What this buys is the population ceiling — eighty
 * wandering animals cost about what eight did, and the tick budget is the real
 * constraint on how alive the valley is allowed to be.
 */
export const THINK_CADENCE = 10;
/**
 * A stable, cheap 0..CADENCE-1 slot for an actor id.
 *
 * Animals are spread across the cadence rather than all thinking on the same
 * tick, because eighty animals thinking together every tenth tick is a spike
 * ten times the size of the average — and p99 is the number the budget is
 * written against.
 */
function thinkSlot(id) {
    let hash = 0;
    for (let i = 0; i < id.length; i += 1)
        hash = (Math.imul(hash, 31) + id.charCodeAt(i)) | 0;
    return Math.abs(hash) % THINK_CADENCE;
}
/** Cheap gate before the body-plan derivation in `isDown`. */
function couldBeDown(vitals, tick) {
    return vitals.dead || vitals.prone || vitals.senselessUntil > tick;
}
/**
 * Does this animal get a full tick's thought?
 *
 * Anything that is not simply grazing does, always: an animal in a fight, a
 * stalk or a flight is an animal a player is looking at, and a beat of lag in a
 * fight is the one place this optimisation would be visible. Everything else is
 * asked once a second unless somebody is close enough to see it.
 */
function thinksNow(ctx, actor, mind, watchers) {
    if (mind.state !== 'graze')
        return true;
    for (const watcher of watchers) {
        if (chebyshev(actor.x, actor.y, watcher.x, watcher.y) <= THINK_NEAR)
            return true;
    }
    return (ctx.tick + thinkSlot(actor.id)) % THINK_CADENCE === 0;
}
/**
 * Every animal, in a fixed order, once a tick.
 *
 * Sorted by id for the reason `combat.ts` sorts its fighters: the order two
 * wolves think in must not decide which of them gets the last place at the
 * front, or a replay could break the tie the other way.
 */
export const mindSystem = {
    name: 'mind',
    phase: 'sim',
    run(ctx) {
        const minds = ctx.world.minds;
        // Gathered once rather than per animal: with eight players and eighty
        // animals this is one pass over the actor table instead of eighty.
        const watchers = [];
        for (const actor of Object.values(ctx.world.actors)) {
            if (isThreat(actor))
                watchers.push(actor);
        }
        for (const id of Object.keys(minds).sort()) {
            const actor = ctx.world.actors[id];
            const mind = minds[id];
            if (!actor || !mind) {
                // Its body left the world. The mind goes with it, or the table grows
                // without bound across a long-lived server.
                delete minds[id];
                continue;
            }
            // Only an animal in an active behaviour can have just been dropped in an
            // encounter. Keep the grazing hot path as cheap as it was: full valleys
            // spend almost all their lives there.
            const combatant = mind.state === 'graze' ? undefined : ctx.world.combat[id];
            if (combatant && couldBeDown(combatant.vitals, ctx.tick)) {
                const plan = ctx.catalogue.plan(combatant.loadout.planId);
                if (plan && isDown(plan, combatant.vitals, ctx.tick)) {
                    // For huntable prey, being put senseless is the end of the hunt.
                    // Convert before thought so it cannot crawl away between the blow and
                    // the carcass pass later in this same tick.
                    const instinct = ctx.catalogue.instinct?.(mind.instinctId);
                    if (actor.kind === 'creature' && instinct?.disposition === 'prey') {
                        die(combatant.vitals, 'wound');
                    }
                    continue;
                }
            }
            if (!thinksNow(ctx, actor, mind, watchers))
                continue;
            think(ctx, actor, mind);
        }
    },
};
/**
 * Leave a body where something died.
 *
 * On the sim phase and driven by the combat tables rather than by the death
 * event, for the reason `joined` is emitted by the rule that causes it: a
 * system that watched events would be a second place that has to learn about
 * every future way of dying. What it watches instead is the one fact that is
 * always true of a corpse — there is a dead combatant standing on a tile.
 */
export const carcassSystem = {
    name: 'carcasses',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant?.vitals.dead || !actor)
                continue;
            // Animals only. People leave a `Corpse` instead (M2-07, `death.ts`) —
            // permanent rather than rotting, and holding their gear, because the walk
            // back to your own body is what the cauldron costs you.
            //
            // This used to say players leave nothing at all, on the grounds that a
            // body on the ground would contradict the rebirth rules. M4-07 settled it
            // the other way: the corpse is the point.
            if (actor.kind !== 'creature')
                continue;
            const carcassId = `carcass:${id}`;
            if (ctx.world.carcasses[carcassId])
                continue;
            ctx.world.carcasses[carcassId] = {
                id: carcassId,
                x: actor.x,
                y: actor.y,
                speciesId: combatant.loadout.planId,
                name: actor.name,
                diedAt: ctx.tick,
            };
            // A kill belongs to the opponent the animal was fighting when it fell.
            // If bleeding finishes it later, the newest wound retains the same
            // provenance and supplies the hunter.
            const latestWound = [...combatant.vitals.wounds].sort((left, right) => right.inflictedAt - left.inflictedAt)[0];
            const killerId = combatant.engagedWith ?? latestWound?.provenance?.attacker?.id ?? null;
            // Release the survivor in this same snapshot. Waiting for next tick's
            // upkeep leaves the UI engaged with a body already turned into a carcass.
            for (const other of Object.values(ctx.world.combat)) {
                if (other.engagedWith === id) {
                    other.engagedWith = null;
                    other.withdrawingUntil = 0;
                }
            }
            // The animal leaves the world; what it was stays on the ground. Doing
            // both here keeps "there is a body" and "there is no longer an animal"
            // one decision rather than two that can disagree.
            delete ctx.world.actors[id];
            delete ctx.world.combat[id];
            delete ctx.world.minds[id];
            ctx.emit({
                type: 'died',
                actors: [id, ...(killerId ? [killerId] : [])],
                refs: ['carcass', combatant.loadout.planId],
                text: `${actor.name} is dead.`,
                audience: 'witnesses',
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/**
 * Put one animal on the ground beside its den.
 *
 * Through the `join` handler, exactly as a player arrives — the same rule that
 * governs the AI in `mind.ts`, and for the same reason. A den that placed an
 * actor by assignment could put a wolf inside a wall, and would be a second
 * definition of what it means to enter the world.
 *
 * Returns the new actor's id, or `null` if there was nowhere to put it: a den
 * hemmed in by bodies waits rather than stacking wolves on one tile.
 */
export function spawnFromDen(ctx, den) {
    const beast = ctx.catalogue.beast?.(den.speciesId);
    if (!beast)
        return null;
    const at = findSpawn(ctx.world, ctx.rng, { x: den.x, y: den.y });
    if (!at)
        return null;
    // Numbered off the den's own tally so the id is a function of world state
    // rather than of how many times anything has been called.
    const actorId = `${den.id}#${den.taken + den.living.length}`;
    if (ctx.world.actors[actorId])
        return null;
    const intent = {
        actorId,
        tick: ctx.tick,
        type: 'join',
        payload: {
            name: beast.name,
            kind: 'creature',
            at,
            loadout: beast.loadout,
            ...(den.packId === null ? {} : { packId: den.packId }),
        },
    };
    if (joinHandler.check?.(intent, ctx) != null)
        return null;
    joinHandler.apply(intent, ctx);
    den.living.push(actorId);
    return actorId;
}
/**
 * The slow half: dens counting their dead and carcasses going off.
 *
 * On the hour, because both are things that happen over days — asking ten times
 * a second whether a wolf is still alive is ten times a second spent on a
 * question whose answer changes about once a week.
 */
export const ecologySystem = {
    name: 'ecology',
    phase: 'hour',
    run(ctx) {
        // The hard ceiling (M3-10). Dozens of warrens sown by biome density can ask
        // for more animals than the tick budget will carry, and the honest place to
        // refuse is here: a den that is not allowed to restock this hour waits, and
        // waits in exactly the way an emptied one does. The alternative — tuning the
        // densities until the total happens to come out right — is a number that
        // silently goes wrong the next time a biome target moves.
        let living = beastsAlive(ctx);
        for (const id of Object.keys(ctx.world.dens).sort()) {
            const den = ctx.world.dens[id];
            if (!den)
                continue;
            reconcileDen(ctx.world, den, ctx.tick);
            if (living >= BEAST_CEILING)
                continue;
            if (denIsDue(den, ctx.tick) && spawnFromDen(ctx, den) !== null)
                living += 1;
        }
        for (const id of Object.keys(ctx.world.carcasses).sort()) {
            const carcass = ctx.world.carcasses[id];
            if (carcass && isGone(carcass, ctx.tick))
                delete ctx.world.carcasses[id];
        }
    },
};
/** How many animals are standing in the valley right now. */
function beastsAlive(ctx) {
    let count = 0;
    for (const actor of Object.values(ctx.world.actors)) {
        if (actor.kind === 'creature')
            count += 1;
    }
    return count;
}
/**
 * Herbivores eating the valley, on the hour (M3-10).
 *
 * The "eats things" half, and it is one hourly system because that is all it
 * needs to be: an animal standing on a patch takes a pick off it exactly as a
 * player forage does, and the patch comes back on M3-03's existing clock. That
 * makes it a real loop rather than flavour — the animals compete with the player
 * for the same meadows, a heavily grazed valley is visibly thinner, and there is
 * finally an honest reason for a stag to be standing where it stands.
 *
 * Hourly rather than per-tick for the reason `floraSystem` is: how much a deer
 * has eaten is a fact about the afternoon, and a per-tick version would strip
 * the valley bare in an afternoon besides.
 */
export const browseSystem = {
    name: 'browse',
    phase: 'hour',
    run(ctx) {
        for (const id of Object.keys(ctx.world.minds).sort()) {
            const mind = ctx.world.minds[id];
            const actor = ctx.world.actors[id];
            if (!mind || !actor)
                continue;
            const instinct = ctx.catalogue.instinct?.(mind.instinctId);
            if (!instinct?.browses)
                continue;
            // Nothing eats in a fight, or while running for its life, or dead.
            if (mind.state !== 'graze' && mind.state !== 'alert')
                continue;
            if (ctx.world.combat[id]?.vitals.dead)
                continue;
            const patch = ctx.world.flora[floraKey(actor.x, actor.y)];
            if (!canForage(patch))
                continue;
            const def = ctx.catalogue.flora?.(patch?.speciesId ?? '');
            if (!def || !patch)
                continue;
            if (!isFodder(ctx, def.itemId))
                continue;
            takeFlora(patch, def, ctx.tick);
            // Archived rather than narrated: forty animals eating is telemetry, and
            // what a player *sees* is that the meadow is thinner than it was.
            ctx.emit({
                type: 'interacted',
                actors: [id],
                refs: ['browse', patch.speciesId],
                audience: 'archive',
                at: { x: actor.x, y: actor.y },
            });
        }
    },
};
/**
 * Tags an animal will not eat.
 *
 * Read off the item the patch yields rather than declared on the flora record,
 * so the rule is stated once and every plant content adds inherits it: a deer
 * will strip a hazel and browse bilberry, and nothing in the valley eats a
 * bundle of firewood or a sheaf of cut reeds.
 */
export const NOT_FODDER = ['fuel', 'material'];
function isFodder(ctx, itemId) {
    const item = ctx.catalogue.item?.(itemId);
    if (!item)
        return false;
    return !item.tags.some((tag) => NOT_FODDER.includes(tag));
}
/** Everything M2-05 installs on the rule set, plus M3-10's browsing. */
export const ECOLOGY_SYSTEMS = [carcassSystem, ecologySystem, browseSystem];
//# sourceMappingURL=beasts.js.map